import { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { Membership } from '../../api/types';
import { useSession } from '../../app/session';
import { AttachmentList } from '../../components/Attachments';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { formatDate } from '../../lib/format';
import { LogbookView } from '../logbook/LogbookView';
import { OrganisationFormDrawer } from './OrganisationFormDrawer';

/**
 * The club, its school and its airport.
 *
 * The nested organisations are shown as their own tabs rather than as separate pages, because
 * to a club they are all one place with three sets of paperwork.
 */
export function OrganisationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/$org/organisation' });
  const { organisation, can } = useSession();
  const org = organisation!.slug;
  const [editing, setEditing] = useState<string | null>(null);

  const tab = search.tab ?? 'profile';
  const setTab = (value: string | null) =>
    void navigate({
      to: '/$org/organisation',
      params: { org },
      search: { tab: value ?? 'profile' },
    });

  const detail = useQuery({
    queryKey: ['organisation', org],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/organisations/{slug}/', { params: { path: { slug: org } } }),
      ),
  });

  const school = detail.data?.children?.find((child) => child.kind === 'FLIGHT_SCHOOL');
  const airport = detail.data?.children?.find((child) => child.kind === 'AIRPORT');

  if (detail.isLoading) return <Loader />;
  const record = detail.data;
  if (!record) return null;

  return (
    <>
      <PageHeader
        title={record.display_name}
        subtitle={t(`organisation.${record.kind}`)}
        actions={
          can('organisation.change') ? (
            <Button
              variant="light"
              leftSection={<IconPencil size={16} />}
              onClick={() => setEditing(record.slug)}
            >
              {t('action.edit')}
            </Button>
          ) : null
        }
      />

      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="profile">{t('nav.organisation')}</Tabs.Tab>
          {school ? <Tabs.Tab value="school">{t('nav.school')}</Tabs.Tab> : null}
          {airport ? <Tabs.Tab value="airport">{t('nav.airport')}</Tabs.Tab> : null}
          <Tabs.Tab value="members">{t('nav.members')}</Tabs.Tab>
          <Tabs.Tab value="logbook">{t('nav.logbook')}</Tabs.Tab>
          <Tabs.Tab value="documents">{t('nav.documents')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'profile' ? (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Card p="md">
            <Group gap="md" mb="md">
              <Avatar src={record.logo} size={56} radius="md">
                {record.short_name?.slice(0, 3) || record.name.slice(0, 3)}
              </Avatar>
              <Stack gap={0}>
                <Text fw={600}>{record.name}</Text>
                <Text fz="sm" c="dimmed">
                  {[record.city, record.country].filter(Boolean).join(', ')}
                </Text>
              </Stack>
            </Group>
            <Table fz="sm" withRowBorders={false} verticalSpacing={4}>
              <Table.Tbody>
                <DetailRow label={t('organisation.kind')} value={t(`organisation.${record.kind}`)} />
                <DetailRow
                  label={t('organisation.headInstructor')}
                  value={record.head_instructor_name ?? undefined}
                />
                <DetailRow label={t('staff.email')} value={record.email ?? undefined} />
                <DetailRow label={t('staff.phone')} value={record.phone ?? undefined} />
                <DetailRow label={t('staff.address')} value={record.address ?? undefined} />
                <DetailRow label={t('organisation.website')} value={record.website ?? undefined} />
                {record.club_profile ? (
                  <>
                    <DetailRow
                      label={t('organisation.registrationNumber')}
                      value={record.club_profile.registration_number ?? undefined}
                    />
                    <DetailRow
                      label={t('organisation.foundedYear')}
                      value={
                        record.club_profile.founded_year
                          ? String(record.club_profile.founded_year)
                          : undefined
                      }
                    />
                  </>
                ) : null}
              </Table.Tbody>
            </Table>
          </Card>

          <Card p="md">
            <Text fw={600} mb="sm">
              {t('common.summary')}
            </Text>
            <SimpleGrid cols={2} spacing="md">
              <Stat label={t('nav.members')} value={record.member_count} />
              <Stat label={t('nav.aircraft')} value={record.aircraft_count} />
            </SimpleGrid>
            {record.children.length ? (
              <Stack gap="xs" mt="md">
                {record.children.map((child) => (
                  <Group key={child.id} justify="space-between">
                    <Text fz="sm">{child.name}</Text>
                    <Badge size="sm" variant="light">
                      {t(`organisation.${child.kind}`)}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            ) : null}
          </Card>
        </SimpleGrid>
      ) : null}

      {tab === 'school' && school ? <SchoolPanel slug={school.slug} /> : null}
      {tab === 'airport' && airport ? <AirportPanel slug={airport.slug} /> : null}
      {tab === 'members' ? <MembersPanel slug={org} /> : null}
      {tab === 'logbook' ? <LogbookView kind="CLUB" subjectId={record.id} /> : null}
      {tab === 'documents' ? (
        <Card p="md">
          <AttachmentList
            target="organisations.organisation"
            targetId={record.id}
            canEdit={can('attachments.add')}
          />
        </Card>
      ) : null}

      {editing ? (
        <OrganisationFormDrawer
          opened
          onClose={() => setEditing(null)}
          slug={editing}
        />
      ) : null}
    </>
  );
}

function SchoolPanel({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const school = useQuery({
    queryKey: ['organisation', slug],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/organisations/{slug}/', { params: { path: { slug } } })),
  });

  if (school.isLoading) return <Loader />;
  const record = school.data;
  const profile = record?.school_profile;

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      <Card p="md">
        <Text fw={600} mb="sm">
          {record?.display_name}
        </Text>
        <Table fz="sm" withRowBorders={false} verticalSpacing={4}>
          <Table.Tbody>
            <DetailRow
              label={t('organisation.approvalType')}
              value={profile?.approval_type ?? undefined}
            />
            <DetailRow
              label={t('organisation.approvalNumber')}
              value={profile?.approval_number ?? undefined}
            />
            <DetailRow
              label={t('organisation.competentAuthority')}
              value={profile?.competent_authority ?? undefined}
            />
            <DetailRow
              label={t('organisation.headOfTraining')}
              value={profile?.head_of_training_name ?? undefined}
            />
            <DetailRow
              label={t('organisation.headInstructor')}
              value={record?.head_instructor_name ?? undefined}
            />
          </Table.Tbody>
        </Table>
      </Card>
      <Card p="md">
        <Text fw={600} mb="sm">
          {t('organisation.approvedCourses')}
        </Text>
        {profile?.approved_courses?.length ? (
          <Group gap="xs" wrap="wrap">
            {profile.approved_courses.map((course: string) => (
              <Badge key={course} variant="light">
                {course}
              </Badge>
            ))}
          </Group>
        ) : (
          <EmptyState title={t('common.none')} />
        )}
      </Card>
      {record ? (
        <Card p="md" style={{ gridColumn: '1 / -1' }}>
          <Text fw={600} mb="sm">
            {t('logbook.SCHOOL')}
          </Text>
          <LogbookView kind="SCHOOL" subjectId={record.id} />
        </Card>
      ) : null}
    </SimpleGrid>
  );
}

function AirportPanel({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const airport = useQuery({
    queryKey: ['organisation', slug],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/organisations/{slug}/', { params: { path: { slug } } })),
  });

  if (airport.isLoading) return <Loader />;
  const record = airport.data;
  const profile = record?.airport_profile;

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card p="md">
          <Group gap="sm" mb="sm">
            <Text fw={600}>{record?.display_name}</Text>
            {profile?.icao_code ? (
              <Badge size="lg" className="identifier">
                {profile.icao_code}
              </Badge>
            ) : null}
          </Group>
          <Table fz="sm" withRowBorders={false} verticalSpacing={4}>
            <Table.Tbody>
              <DetailRow
                label={t('organisation.iataCode')}
                value={profile?.iata_code ?? undefined}
              />
              <DetailRow
                label={t('organisation.localIdentifier')}
                value={profile?.local_identifier ?? undefined}
              />
              <DetailRow
                label={t('organisation.elevation')}
                value={
                  profile?.elevation_ft !== null && profile?.elevation_ft !== undefined
                    ? String(profile.elevation_ft)
                    : undefined
                }
              />
              <DetailRow
                label={t('organisation.radioFrequency')}
                value={profile?.radio_frequency ?? undefined}
              />
              <DetailRow
                label={t('organisation.operatingHours')}
                value={profile?.operating_hours ?? undefined}
              />
              <DetailRow
                label={t('organisation.airportManager')}
                value={profile?.airport_manager ? String(profile.airport_manager) : undefined}
              />
            </Table.Tbody>
          </Table>
        </Card>
        <Card p="md">
          <Text fw={600} mb="sm">
            {t('organisation.runways')}
          </Text>
          {profile?.runways?.length ? (
            <Table fz="sm">
              <Table.Tbody>
                {profile.runways.map((runway: Record<string, unknown>, index: number) => (
                  <Table.Tr key={index}>
                    <Table.Td fw={600} className="identifier">
                      {String(runway.designator ?? '')}
                    </Table.Td>
                    <Table.Td ff="monospace">
                      {String(runway.length_m ?? '')} × {String(runway.width_m ?? '')} m
                    </Table.Td>
                    <Table.Td c="dimmed">{String(runway.surface ?? '')}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <EmptyState title={t('common.none')} />
          )}
        </Card>
      </SimpleGrid>
      {record ? (
        <Card p="md">
          <Text fw={600} mb="sm">
            {t('logbook.AIRPORT')}
          </Text>
          <LogbookView kind="AIRPORT" subjectId={record.id} />
        </Card>
      ) : null}
    </Stack>
  );
}

function MembersPanel({ slug }: { slug: string }) {
  const { t } = useTranslation();

  const members = useQuery({
    queryKey: ['organisation', slug, 'members'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/organisations/{slug}/members/', { params: { path: { slug } } }),
      ),
  });

  const columns = useMemo<ColumnDef<Membership, unknown>[]>(
    () => [
      {
        accessorKey: 'person_name',
        header: t('common.name'),
        cell: ({ row }) => (
          <Text fz="sm" fw={600}>
            {row.original.person_name}
          </Text>
        ),
      },
      {
        accessorKey: 'membership_number',
        header: t('organisation.membershipNumber'),
        meta: { width: 150, mono: true },
      },
      {
        accessorKey: 'role_codes',
        header: t('organisation.roles'),
        cell: ({ row }) => (
          <Group gap={4} wrap="wrap">
            {row.original.role_codes.map((code) => (
              <Badge key={code} size="xs" variant="light">
                {code}
              </Badge>
            ))}
          </Group>
        ),
      },
      {
        accessorKey: 'joined_on',
        header: t('common.validFrom'),
        meta: { width: 120 },
        cell: ({ row }) => formatDate(row.original.joined_on),
      },
      {
        accessorKey: 'is_primary',
        header: '',
        meta: { width: 90 },
        cell: ({ row }) =>
          row.original.is_primary ? (
            <Badge size="xs" color="teal" variant="light">
              {t('common.organisation')}
            </Badge>
          ) : null,
      },
    ],
    [t],
  );

  return (
    <DataTable
      data={members.data ?? []}
      columns={columns}
      isLoading={members.isLoading}
      emptyTitle={t('nav.members')}
      minWidth={800}
    />
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <Table.Tr>
      <Table.Td c="dimmed" w={180}>
        {label}
      </Table.Td>
      <Table.Td>{value}</Table.Td>
    </Table.Tr>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Stack gap={0}>
      <Text fz="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fz={26} fw={700} lh={1.2}>
        {value}
      </Text>
    </Stack>
  );
}
